import { useState } from 'react'

import './_singleSetting.scss'
import {
    DndContext,
    type DragEndEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors
} from '@dnd-kit/core'
import {
    restrictToParentElement,
    restrictToVerticalAxis
} from '@dnd-kit/modifiers'
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import SelectCategoriesModal from 'components/UI/Modal/SelectCategoriesModal/SelectCategoriesModal'
import { useModalContext } from 'context/modalContext'
import { Icons } from 'helpers/icons'
import { putProfileSettings } from 'services/endpoints/settings/account/profileSettings'
import { useUserProfile } from 'store'
import { type ICategory } from 'types/interfaces/ITypes'

import { SuccessfulChange } from '../SuccessfulChange'

type CategoryType = {
    id: number
    category_id: number | null
    name: string
    image: string
    category?: ICategory | null
}

const getPlaceholderImage = (id: number) =>
    `https://picsum.photos/seed/chat-strategy-${id}/200`

const SortableCategoryCard = ({
    category,
    index,
    onClick
}: {
    category: CategoryType
    index: number
    onClick: () => void
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: category.id
    })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 100 : 1,
        opacity: isDragging ? 0.9 : 1
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className='chat-strategy-card'
            onClick={onClick}
        >
            <div className='chat-strategy-card__order'>
                {index + 1}
            </div>

            {category.category_id ? (
                <img
                    src={category.image}
                    alt=''
                    className='chat-strategy-card__avatar'
                />
            ) : (
                <div className='chat-strategy-card__addIcon'>
                    +
                </div>
            )}

            <div className='chat-strategy-card__name'>
                {category.name}
            </div>

            <div
                className='chat-strategy-card__drag'
                onClick={e => e.stopPropagation()}
                {...attributes}
                {...listeners}
            >
                ☰
            </div>
        </div>
    )
}

export const ChatStrategy = () => {
    const [page, setPage] = useState<number>(1)

    const navigate = useNavigate()
    const userData = useUserProfile()
    const modalContext = useModalContext()

    const queryClient = useQueryClient()
    const { t } = useTranslation()

    const [categories, setCategories] = useState<CategoryType[]>([
        {
            id: 1,
            category_id: null,
            name: 'Add Category',
            image: getPlaceholderImage(1),
            category: null
        },
        {
            id: 2,
            category_id: null,
            name: 'Add Category',
            image: getPlaceholderImage(2),
            category: null
        },
        {
            id: 3,
            category_id: null,
            name: 'Add Category',
            image: getPlaceholderImage(3),
            category: null
        }
    ])

    const sensors = useSensors(useSensor(PointerSensor))

    const handleDragStart = () => {
        document.body.style.overflow = 'hidden'
    }

    const handleDragCancel = () => {
        document.body.style.overflow = ''
    }

    const handleDragEnd = (event: DragEndEvent) => {
        document.body.style.overflow = ''

        const { active, over } = event

        if (!over || active.id === over.id) {
            return
        }

        setCategories(items => {
            const oldIndex = items.findIndex(
                item => item.id === active.id
            )

            const newIndex = items.findIndex(
                item => item.id === over.id
            )

            return arrayMove(items, oldIndex, newIndex)
        })
    }

    const MAX_ITEMS = 10

    const addCategoryItem = () => {
        setCategories(items => {
            if (items.length >= MAX_ITEMS) {
                return items
            }

            const nextId =
                items.length > 0
                    ? Math.max(...items.map(item => item.id)) + 1
                    : 1

            return [
                ...items,
                {
                    id: nextId,
                    category_id: null,
                    name: 'Add Category',
                    image: '',
                    category: null
                }
            ]
        })
    }

    const openCategoryModal = (strategyItem: CategoryType) => {
        modalContext.addModal(
            'Select Category',
            <SelectCategoriesModal
                selectedCategories={
                    strategyItem.category ? [strategyItem.category] : []
                }
                onlyOne={true}
                hideAllCategory={true}
                hidePublicCategories={true}
                hideVisibilityBadges={true}
                onCancel={() => {
                    modalContext.clearModal()
                }}
                onSave={(selectedCategories: ICategory[]) => {
                    const selectedCategory = selectedCategories[0]

                    if (!selectedCategory) {
                        modalContext.clearModal()
                        return
                    }

                    setCategories(items =>
                        items.map(item => {
                            if (item.id !== strategyItem.id) {
                                return item
                            }

                            return {
                                ...item,
                                category_id: Number(selectedCategory.id),
                                name: selectedCategory.name,
                                image: getPlaceholderImage(item.id),
                                category: selectedCategory
                            }
                        })
                    )

                    modalContext.clearModal()
                }}
            />
        )
    }

    const submitFunction = useMutation(
        async () =>
            await putProfileSettings({
                chat_strategy: JSON.stringify(
                    categories
                        .filter(category => category.category_id !== null)
                        .map((category, index) => ({
                            id: category.id,
                            category_id: category.category_id,
                            order: index + 1
                        }))
                )
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('user-profile')
                queryClient.refetchQueries(['profile', `${userData?.id}`])
                setPage(2)
            },
            onError: err => {
                console.error(err)
                addToast('error', t('error:errorSomethingWentWrong'))
            }
        }
    )

    const renderPage = () => {
        if (page === 1) {
            return (
                <div className='chat-strategy'>
                    <p className='chat-strategy__subtitle'>
                        Drag to reorder the category sending order
                    </p>

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        modifiers={[
                            restrictToVerticalAxis,
                            restrictToParentElement
                        ]}
                        onDragStart={handleDragStart}
                        onDragCancel={handleDragCancel}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={categories.map(
                                category => category.id
                            )}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className='chat-strategy__list'>
                                {categories.map((category, index) => (
                                    <SortableCategoryCard
                                        key={category.id}
                                        category={category}
                                        index={index}
                                        onClick={() =>
                                            openCategoryModal(category)
                                        }
                                    />
                                ))}

{categories.length < MAX_ITEMS && (
    <div
        className='chat-strategy-card'
        onClick={addCategoryItem}
    >
        <div className='chat-strategy-card__order chat-strategy-card__order--add'>
            +
        </div>

        <div className='chat-strategy-card__name'>
            Add Item
        </div>
    </div>
)}
                            </div>
                        </SortableContext>
                    </DndContext>

                    <Button
                        text={t('save')}
                        color='black'
                        font='mont-16-bold'
                        width='20'
                        height='5'
                        clickFn={() => {
                            submitFunction.mutate()
                        }}
                        customClass='chat-strategy-save-button'
                    />
                </div>
            )
        }

        return (
            <>
                <SuccessfulChange
                    img={Icons.avatar_big}
                    text='Successfully updated chat strategy'
                />

                <Button
                    text={t('close')}
                    color='black'
                    font='mont-16-bold'
                    width='20'
                    height='5'
                    clickFn={() => {
                        navigate('/settings/general/profile-settings')
                    }}
                    customClass='mini-bio-save-button'
                />
            </>
        )
    }

    return <>{renderPage()}</>
}